//
// SELECTORS, these are functions designed to work directly on and with the
// redux data store
//

import { createSelector } from '@reduxjs/toolkit';
import type { List as ImmutableList, Map as ImmutableMap, RecordOf } from 'immutable';
import { List, Map } from 'immutable';
import { DateTime } from 'luxon';
import { LruObjectCache, createCachedSelector } from 're-reselect';

import { accountsSelectors, accountsUtils } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { scheduledTransactionsSelectors } from 'app-components/flux/scheduled-transactions';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import type {
  SplitItemProps,
  TransactionProps,
  TransactionRecord,
} from 'app-components/flux/transactions/transactionsTypes';
import { getLogger } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';
import type CoreRootState from 'app-components/utils/redux-store/rootState';

import { getOptimalCacheFromObject, setOptimalCacheFromObject } from 'data/optimalCacheManager';
import { memoizeWithKey, memoizeWithKeyRetrieveCache } from 'utils/memoizeWithKey';
import { getOrCreateStringFromObject } from 'utils/objectToKeyUtils';

import { balanceSelectors } from 'app-components/flux/calculated-balances';

import { optimalCacheCompareFn, optimalCacheProcessFn } from 'data/transactionList/optimalCacheFunctions';

import type { RootState } from 'store';

import { dateSelectors } from 'app-components/flux/date';
import { groupTxnListByAmount, groupTxnListByDate, groupTxnListByString } from './grouping';
import { sortTransactions } from './sorting';

const log = getLogger('data/transactionList/selectors');

type TxnList = ImmutableList<TransactionRecord>;
type TxnGroupedMap = ImmutableMap<string, TxnList>;

//-----------------------------------------------
//-----------------------------------------------

// getTransactionListForAccountIds
//
// This selector expects the following properties:
//   - an array of accountIds
//   - sortBy property
//   - sortOrder property
//   - filter function that if defined is provided the transaction list to filter. null
//     functions as the identity filter (show all)
//
// Returns an immutable list of transactions from accounts specified by the
// accountIds array.

export const getIsProtectedTxn = createSelector(
  accountsSelectors.getQBPAccountIds,
  (_state: RootState, props: { txn?: TransactionRecord | null }) => props.txn,
  (qbpAccountIds, txn) => {
    if (!txn) return false;

    return qbpAccountIds?.includes(txn.accountId);
  },
);

export const getTotalNumberOfSplitsWithAllocations = createSelector(
  (_state: RootState, props: { txn?: TransactionRecord | TransactionProps | null }) => props.txn,
  (state: RootState) => transactionsSelectors.getTransactionsByAccountId(state),
  (txn, txns) => {
    if (!txn || !txns) return 0;
    if (!txn.split) return 0;

    const items = txn.split.items ?? [];
    const itemsArray: Array<RecordOf<SplitItemProps> | SplitItemProps> = Array.isArray(items) ? items : items.toArray();
    return itemsArray.reduce((acc: number, split: RecordOf<SplitItemProps> | SplitItemProps) => {
      const transferTx = transactionsUtils.findMatchingTransferTx(
        txn,
        (split.coa?.id ? txns.get(split.coa.id) : undefined) ?? Map<string, TransactionRecord>(),
        split.amount,
        split.transfer,
      );
      const allocations = transferTx?.txn?.allocations;
      return acc + (allocations && 'size' in allocations && allocations.size > 0 ? 1 : 0);
    }, 0);
  },
);

interface TransactionListForAccountIdsProps {
  accountIds?: string[] | null;
  sortBy?: string;
  sortOrder?: string;
  filterFn?: (txns: TxnList) => TxnList;
  overrideTxns?: TxnList | null;
  id?: string;
}

export const getTransactionListForAccountIds = createSelector(
  (_state: RootState, props: TransactionListForAccountIdsProps) => props.accountIds,
  (_state: RootState, props: TransactionListForAccountIdsProps) => props.sortBy || 'date',
  (_state: RootState, props: TransactionListForAccountIdsProps) => props.sortOrder || 'sortDescending',
  (_state: RootState, props: TransactionListForAccountIdsProps) => props.filterFn,
  (_state: RootState, props: TransactionListForAccountIdsProps) => props.overrideTxns,
  (_state: RootState, props: TransactionListForAccountIdsProps) => props.id,
  transactionsSelectors.getTransactionsStore,
  accountsSelectors.getAccountsById,
  transactionsSelectors.getTransactionsByAccountId,
  transactionsSelectors.getTransactionsByAccountIdsSelectors,
  balanceSelectors.getBalancesByAccountIds,
  featureFlagsSelectors.getFeatureFlags,

  (function combinerClosure() {
    return (
      accountIds: string[] | null | undefined,
      sortBy: string,
      sortOrder: string,
      filterFn: ((txns: TxnList) => TxnList) | undefined,
      overrideTxns: TxnList | null | undefined,
      id: string | undefined,
      transactionsStore: ReturnType<typeof transactionsSelectors.getTransactionsStore>,
      accountsById: ReturnType<typeof accountsSelectors.getAccountsById>,
      transactionsByAccountIds: ReturnType<typeof transactionsSelectors.getTransactionsByAccountId>,
      transactionsByAccountIdsSelectors: ReturnType<typeof transactionsSelectors.getTransactionsByAccountIdsSelectors>,
      balancesById: ReturnType<typeof balanceSelectors.getBalancesByAccountIds>,
      featureFlags: ReturnType<typeof featureFlagsSelectors.getFeatureFlags>,
    ) => {
      const cacheObject = {
        name: 'txList',
        accountIds,
        sortBy,
        sortOrder,
        filterFn,
        overrideTxns,
        id,
        dateOnAfter: transactionsStore.dateOnAfter,
        accountsById,
        transactionsByAccountIdsSelectors,
        featureFlags,
      };
      const strictCacheObject = {
        ...cacheObject,
        transactionsByAccountIds,
        balancesById,
      };
      // if we are forcing a cache break, then skip cache processing, and go straight to regeneration,
      const cachedValues = getOptimalCacheFromObject(
        transactionsStore,
        'txList',
        cacheObject,
        strictCacheObject,
        (oldTxn: TransactionRecord, newTxn: TransactionRecord) =>
          optimalCacheCompareFn(oldTxn, newTxn, sortBy, filterFn),
        (cache: unknown, newTxn: TransactionRecord) => optimalCacheProcessFn(cache, newTxn, filterFn),
        10,
      );
      if (cachedValues) log.debug('===================== TXLIST CACHE HIT =======================');
      if (cachedValues) return cachedValues;

      log.debug('===================== TXLIST CACHE MISS =======================');

      let accountIdsList: Array<string | null | undefined> | string[] = accountIds ?? [];
      if (!accountIds || accountIds.length === 0) {
        accountIdsList = accountsById
          .map((x) => x.id)
          .valueSeq()
          .toArray();
      }

      let transactionsForAccountIds: TxnList;
      if (overrideTxns) {
        transactionsForAccountIds = overrideTxns;
      } else {
        transactionsForAccountIds = List();

        accountIdsList.forEach((accountId) => {
          if (!accountId) return;
          const acct = accountsById.get(accountId);

          const hideTxns = accountsUtils.transactionsNotSupportedForAccountId(
            accountId,
            featureFlags.get('hideConnectedLoanTransactions'),
            store?.getState() as CoreRootState,
          );

          if (acct && !hideTxns) {
            const transactionsForAccountIdSelector = transactionsByAccountIdsSelectors.get(accountId);

            const transactionsForAccountId = transactionsForAccountIdSelector?.(transactionsByAccountIds);

            // Don't show scheduled transaction reminder in register (they go to upcoming section)
            const filteredTransactions = transactionsForAccountId?.filter(
              (txn: TransactionRecord) => !transactionsUtils.isUnacceptedScheduledTxn(txn),
            );
            if (filteredTransactions) {
              transactionsForAccountIds = transactionsForAccountIds.concat(filteredTransactions) as TxnList;
            }
          }
        });
      }

      let filteredTxns: TxnList = filterFn ? filterFn(transactionsForAccountIds) : transactionsForAccountIds;
      if (sortBy === 'reviewed') {
        filteredTxns = filteredTxns.filter((txn) => (sortOrder === 'ascending' ? !txn.isReviewed : txn.isReviewed));
      }

      // filter out any future transactions and DUE/OVERDUE scheduled transactions
      // but do not do this if caller sent in their own txn list (override txns)
      if (!overrideTxns) {
        const today = DateTime.local().toISODate();
        filteredTxns = filteredTxns.filter(
          (txn) =>
            (DateTime.fromISO(txn.postedOn).toISODate() ?? '') <= (today ?? '') &&
            !transactionsUtils.isUnacceptedScheduledTxn(txn),
        );
      }
      const sortedTxns = sortTransactions(filteredTxns, sortBy, sortOrder);

      let groupedTxns;
      switch (sortBy) {
        // TODO: remove 'status' and 'date' case once old register is removed/updated
        case 'date':
        case 'postedOn':
          groupedTxns = groupTxnListByDate(sortedTxns, featureFlags, false, sortOrder);
          break;

        case 'status':
        case 'state':
          groupedTxns = groupTxnListByDate(sortedTxns, featureFlags, true, sortOrder);
          break;

        case 'category':
        case 'payee':
        case 'check':
          groupedTxns = groupTxnListByString(sortedTxns, sortBy);
          break;

        case 'reviewed':
          groupedTxns = groupTxnListByDate(sortedTxns, featureFlags, true, sortOrder);
          break;

        case 'amount':
          groupedTxns = groupTxnListByAmount(sortedTxns);
          break;

        case 'action':
          groupedTxns = sortedTxns.groupBy(
            (transaction: TransactionRecord) =>
              transactionsUtils.InvestmentTxnActionsByType[
                transaction.investmentTxnType as keyof typeof transactionsUtils.InvestmentTxnActionsByType
              ] || 'Others',
          );
          break;

        case 'quantity':
        case 'price':
        case 'commission':
          groupedTxns = sortedTxns.groupBy(() => '');
          break;

        default:
          groupedTxns = groupTxnListByString(sortedTxns, 'payee');
      }

      setOptimalCacheFromObject(transactionsStore, 'txList', cacheObject, strictCacheObject, groupedTxns, 10);
      return groupedTxns;
    };
  })(),
);

interface LesserBilledTransactionsProps {
  isBill?: boolean;
  isTransfer?: boolean;
}

/*
 * GetLesserBilledTransactions
 */
export const getLesserBilledTransactions = createSelector(
  (_state: RootState, props: LesserBilledTransactionsProps) => props.isBill,
  (_state: RootState, props: LesserBilledTransactionsProps) => props.isTransfer,
  scheduledTransactionsSelectors.getScheduledTransactions,
  transactionsSelectors.getTransactionsByTextFilter,
  categoriesSelectors.getIncomeCOAIds,
  (isBill, isTransfer, scheduledTxns, txnList, incomeCOAs) => {
    const endDate = DateTime.local();
    const startDate = endDate.minus({ months: 6 });
    const txns = txnList.filter((txn) => {
      // currently, only cash-flow txns can be recurring
      if (txn.type !== 'CASH_FLOW') return false;

      // we also don't support split-txns
      if (transactionsUtils.isSplitTxn(txn)) return false;

      // if txn is already linked to a scheduled txn, do not include it
      if (txn.stModelId && scheduledTxns && scheduledTxns.has(txn.stModelId)) return false;

      const isIncome = transactionsUtils.isIncomeFromTxn(txn, incomeCOAs);
      if (isBill !== undefined && (isBill ? isIncome : !isIncome)) return false;

      if (isTransfer) {
        // if not an internal or external transfer and not a credit-card payment, do not include it
        if (txn?.coa?.type !== 'ACCOUNT' && txn?.coa?.id !== '2' && txn?.coa?.id !== '3') return false;

        // transactionsUtils.findMatchingTransferTxn()
      }

      if (txn.coa?.type === 'BALANCE_ADJUSTMENT' && txn.coa?.id === '0') {
        // exclude opening balance
        return false;
      }

      const txnDate = DateTime.fromISO(txn.postedOn);
      if (txnDate < startDate || txnDate > endDate) return false;

      return true;
    });

    return sortTransactions(txns);
  },
);

export const transactionListMetrics = createCachedSelector(
  (_state: RootState, transactionList: TxnGroupedMap) => transactionList,
  (txnList: TxnGroupedMap) => {
    let numTxns = 0;
    let sumTxns = 0;
    let earliestDate = DateTime.local().toISODate();
    let reviewedCount = 0;
    let hasScheduledInstances = false;

    txnList.forEach((section) => {
      numTxns += section.size;
      section.forEach((txn) => {
        sumTxns += Number(txn.amount);
        if (earliestDate && txn.postedOn < earliestDate) {
          earliestDate = txn.postedOn;
        }
        reviewedCount += txn.isReviewed ? 1 : 0;
        hasScheduledInstances = hasScheduledInstances || transactionsUtils.isUnacceptedScheduledTxn(txn);
      });
    });
    return { numTxns, sumTxns, earliestDate, reviewedCount, hasScheduledInstances };
  },
)({
  // `transactionList` (the grouped map) doesn't normally carry an `accountIds` field; this mirrors
  // the pre-existing (loosely typed) cache key derivation as-is.
  keySelector: (_state: RootState, props: TxnGroupedMap & { accountIds?: { toString(): string } }) =>
    props.accountIds ? props.accountIds.toString() : 'all',
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

export const getDateGroupedTransactions = createSelector(
  (txns: TxnList) => txns,
  (_txns: TxnList, date: string) => date,
  (_txns: TxnList, _date: string, schTxns: unknown) => schTxns,
  (function combinerClosure() {
    return (txns: TxnList, date: string, schTxns: unknown) => {
      const key = getOrCreateStringFromObject(
        'dateGroupedTxns',
        { txns, date, schTxns },
        5,
        memoizeWithKeyRetrieveCache, // fn used for garbage collection for unusued object strings
      );
      return memoizeWithKey(
        'dateGroupedTxns',
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        () => futureFilteredTxns(txns, date, schTxns)?.groupBy((txn) => DateTime.fromISO(txn.postedOn).toISODate()),
        key,
        5,
      );
    };
  })(),
);

interface GroupedRecentAndFutureAccountTransactionsProps {
  dateRange: number;
}

export const getGroupedRecentAndFutureAccountTransactions = createSelector(
  transactionsSelectors.getTransactionsByAccountIdRaw,
  dateSelectors.getCurrentDate,
  (_state: RootState, props: GroupedRecentAndFutureAccountTransactionsProps) => props.dateRange,
  (txnsByAccountId, currentDate, dateRange) => {
    const endDate = currentDate.plus({ days: dateRange }).toISODate();
    const pastCutoff = currentDate.minus({ days: 10 }).toISODate();
    const todayDate = currentDate.toISODate();

    const cacheKey = getOrCreateStringFromObject(
      'recent-grouped-txns',
      {
        name: 'recentAcctTxns',
        pastCutoff: pastCutoff,
        txnsByAccountId,
      },
      5,
      memoizeWithKeyRetrieveCache, // fn used for garbage collection for unusued object strings
    );

    return memoizeWithKey(
      'recent-grouped-txns',
      () => {
        const todayTxnArr: TransactionRecord[] = []; // for due or overdue txns
        const groupedTxns = Map<string, unknown>().withMutations((accountMapSetter) => {
          txnsByAccountId.forEach((account, key) => {
            const ungroupedTxns = account.filter((txn) => {
              if (txn.id === txn.clientId) {
                // filter out optimistically created manual transactions because they crash AMcharts
                return false;
              }
              const refundState = transactionsUtils.getRefundState(txn);
              if (
                refundState === transactionsUtils.RefundState.EXPECTED ||
                refundState === transactionsUtils.RefundState.OVERDUE
              ) {
                return true;
              }
              const txnDate = txn.postedOn;
              const info = transactionsUtils.getTxnStateInfo(txn);
              if (info.status === 'DUE' || info.status === 'OVERDUE' || transactionsUtils.isPlannedA2ATransfer(txn)) {
                if (txnDate < (todayDate ?? '')) {
                  todayTxnArr.push(txn);
                }
                return true;
              }
              return Boolean(txnDate && txnDate < (endDate ?? '') && txnDate > (pastCutoff ?? ''));
            });
            const ret = ungroupedTxns?.groupBy((txn) => txn.postedOn);
            accountMapSetter.set(key, ret);
          });
        });
        return {
          todayTxnArr,
          groupedTxns,
        };
      },
      cacheKey,
      5,
    );

    // log.debug("REGENERATING RECENT ACCOUNT TRANSACTIONS", accountId);

    // Because of the way that the projected balances graph is situated, we want separation and grouping of transactions by account
  },
);

export const processCalendarTxns = createSelector(
  (txns: TxnGroupedMap) => txns,
  (txns: TxnGroupedMap) => txns.groupBy(() => '9999-99:Pending'),
);

/** *****************************************************************************************
 * HELPER FUNCTIONS
 ******************************************************************************************** */
// This selector is basically used for cache (could use memoize instead)
export const futureFilteredTxns = (txns: TxnList, date: string, schTxns: unknown) =>
  txns.filter((txn) => {
    const txnDate = DateTime.fromISO(txn.postedOn);
    if (schTxns) {
      return (
        (txn.source === 'USER_ENTERED' && (txnDate.diff(DateTime.local(), 'days').toObject().days ?? 0) >= 0) ||
        txn.source === 'SCHEDULED_TRANSACTION_PENDING'
      );
    }
    return (
      // adding this so we can have the 'waiting' button
      txn.source === 'USER_ENTERED' || txn.source === 'SCHEDULED_TRANSACTION_PENDING'
    );
  });
